import React, { useState, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { useSelector } from 'react-redux';
import Button from '../../../../../components/ComponentsV1/Button';
import InputV1 from '../../../../../components/ComponentsV1/InputV1';
import { useClient } from '../../../../../api/api-client';
import { IVA_POLICIES_ENDPOINTS, inputClassName } from '../constants';
import { toQuery, readErrorMessage } from '../helpers';
import { useComplaintTypes } from '../useComplaintTypes';
import IvaSkeleton from './IvaSkeleton';
import GuidelineForm from './GuidelineForm';
import ConfirmModal from './ConfirmModal';
import Collapsible from './Collapsible';
import IvaTabBar from './IvaTabBar';

const StepGuidelineView = ({ label, steps }) => {
  if (!Array.isArray(steps) || steps.length === 0) return null;
  const sorted = steps
    .slice()
    .sort((a, b) => (a.step_number || 0) - (b.step_number || 0));
  return (
    <div className='tw-w-full tw-min-w-0 tw-flex tw-flex-col tw-gap-2'>
      <div className='tw-text-[1.2rem] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-grey-500'>
        {label}
      </div>
      <div className='tw-w-full tw-min-w-0 tw-flex tw-flex-col tw-gap-2'>
        {sorted.map((step, idx) => (
          <Collapsible
            key={idx}
            title={
              sorted.length > 1
                ? `Step ${step.step_number}: ${step.title || ''}`
                : step.title || 'Prompt'
            }
            count={(step.guidelines || []).length}
          >
            <ul className='tw-m-0 tw-pl-6 tw-flex tw-flex-col tw-gap-1 tw-text-[1.3rem] tw-leading-relaxed tw-text-grey-700'>
              {(step.guidelines || []).map((g, gi) => (
                <li key={gi}>{g}</li>
              ))}
            </ul>
          </Collapsible>
        ))}
      </div>
    </div>
  );
};

const BucketBody = ({ bucket }) => {
  if (!bucket) {
    return (
      <div className='tw-text-[1.3rem] tw-text-grey-400 tw-py-6 tw-text-center'>
        No guidelines configured.
      </div>
    );
  }
  return (
    <div className='tw-w-full tw-min-w-0 tw-flex tw-flex-col tw-gap-4 tw-max-h-[520px] tw-overflow-y-auto tw-pr-2'>
      <StepGuidelineView
        label='Summary Guidelines'
        steps={bucket.media_summarization_guidelines}
      />
      <StepGuidelineView
        label='Verification Guidelines'
        steps={bucket.step_by_step_verification_guidelines}
      />
    </div>
  );
};

const GuidelineCard = ({ complaintType, guideline, onPublished, onEdit }) => {
  const client = useClient();
  const updatedBy = useSelector(
    (state) => state.authState?.userDetails?.crm_user_id,
  );
  const [isPublishing, setIsPublishing] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [activeBucket, setActiveBucket] = useState('target');

  const buckets = [
    { key: 'target', label: 'Target Group', data: guideline.target_group },
    { key: 'control', label: 'Control Group', data: guideline.control_group },
  ];
  const activeData =
    buckets.find((b) => b.key === activeBucket)?.data ||
    guideline.target_group ||
    guideline.control_group;

  const handlePublish = useCallback(async () => {
    setShowConfirm(false);
    setIsPublishing(true);
    try {
      await client(IVA_POLICIES_ENDPOINTS.PUBLISH, {
        data: {
          complaint_type: complaintType.trim(),
          lookup_type: guideline.lookup_type,
          lookup_id: guideline.lookup_id,
          updated_by: String(updatedBy || ''),
        },
      });
      toast.success('Published Target Group to Control Group');
      if (onPublished) onPublished();
    } catch (error) {
      toast.error(readErrorMessage(error));
    } finally {
      setIsPublishing(false);
    }
  }, [client, complaintType, guideline, onPublished]);

  return (
    <div className='tw-bg-white tw-p-8 tw-rounded-2xl tw-border tw-border-solid tw-border-grey-200 tw-flex tw-flex-col tw-gap-5'>
      <div className='tw-flex tw-items-center tw-justify-between tw-gap-4'>
        <div className='tw-flex tw-items-center tw-gap-3'>
          <span className='tw-text-[1.5rem] tw-font-medium tw-text-grey-900'>
            {guideline.lookup_type} : {guideline.lookup_id}
          </span>
          {guideline.reason_code && (
            <span className='tw-text-[1.2rem] tw-px-3 tw-py-1 tw-rounded-lg tw-bg-yellow-200/80 tw-text-zinc-700'>
              {guideline.reason_code}
            </span>
          )}
        </div>
        <div className='tw-flex tw-gap-3'>
          <Button
            title='Edit'
            type='secondary'
            onClick={() =>
              onEdit({
                lookup_type: guideline.lookup_type,
                lookup_id: guideline.lookup_id,
                ...(activeData || {}),
              })
            }
          />
          <Button
            title='Publish'
            onClick={() => setShowConfirm(true)}
            disabled={isPublishing}
            loading={isPublishing}
          />
        </div>
      </div>
      <div className='tw-flex tw-flex-col tw-gap-4'>
        <IvaTabBar
          tabs={buckets.map((b) => ({ key: b.key, title: b.label }))}
          activeKey={activeBucket}
          onChange={setActiveBucket}
        />
        <BucketBody bucket={activeData} />
      </div>
      {showConfirm && (
        <ConfirmModal
          title='Publish Guideline'
          message={`This will overwrite the Control Group with the Target Group for ${guideline.lookup_type} : ${guideline.lookup_id}. Are you sure?`}
          onConfirm={handlePublish}
          onCancel={() => setShowConfirm(false)}
          loading={isPublishing}
        />
      )}
    </div>
  );
};

const GuidelinesSection = () => {
  const client = useClient();
  const [complaintType, setComplaintType] = useState('');
  const [l0, setL0] = useState('');
  const [guidelines, setGuidelines] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const { options: complaintTypeOptions, isLoading: metadataLoading } =
    useComplaintTypes();

  const handleComplaintTypeChange = useCallback((opt) => {
    setComplaintType(opt?.value || '');
    setGuidelines(null);
    setEditing(null);
    setShowCreate(false);
  }, []);

  const handleL0Change = useCallback((e) => {
    setL0(e.target.value);
    setGuidelines(null);
    setEditing(null);
    setShowCreate(false);
  }, []);

  const fetchGuidelines = useCallback(async () => {
    if (!complaintType.trim()) {
      toast.error('Complaint type is required');
      return;
    }
    setIsLoading(true);
    setEditing(null);
    setShowCreate(false);
    try {
      const response = await client(
        `${IVA_POLICIES_ENDPOINTS.FETCH_BY_COMPLAINT_TYPE}${toQuery({
          complaint_type: complaintType,
          category: l0,
        })}`,
      );
      const data = Array.isArray(response) ? response : response?.data ?? response;
      setGuidelines(Array.isArray(data) ? data : [data].filter(Boolean));
    } catch (error) {
      setGuidelines(null);
      toast.error(readErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, [client, complaintType, l0]);

  const visibleGuidelines = guidelines;

  return (
    <div className='tw-flex tw-flex-col tw-gap-6'>
      <div className='tw-bg-white tw-p-8 tw-rounded-2xl tw-border tw-border-solid tw-border-grey-200 tw-flex tw-flex-col tw-gap-4'>
        <div className='tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-6'>
          <div className='tw-flex tw-flex-col tw-gap-2'>
            <label className='tw-text-[1.4rem] tw-font-medium tw-text-grey-800'>
              Complaint Type<span className='tw-text-red-500'>*</span>
            </label>
            <InputV1
              loading={metadataLoading}
              options={complaintTypeOptions}
              placeholder='Choose complaint type'
              multiple={false}
              isSearchable
              closeOnSelect
              onSelect={handleComplaintTypeChange}
              value={complaintType}
              style={{ width: '100%', display: 'block' }}
            />
          </div>
          <div className='tw-flex tw-flex-col tw-gap-2'>
            <label className='tw-text-[1.4rem] tw-font-medium tw-text-grey-800'>
              Category Id (L0)
            </label>
            <input
              className={inputClassName}
              placeholder='Optional — filter by L0 category id'
              value={l0}
              autoComplete='off'
              onChange={handleL0Change}
            />
          </div>
        </div>
        <div className='tw-flex tw-gap-4 tw-items-center'>
          <Button
            title='Fetch'
            onClick={fetchGuidelines}
            disabled={isLoading}
            loading={isLoading}
          />
          <Button
            title='New Guideline'
            type='secondary'
            onClick={() => {
              setEditing(null);
              setShowCreate((prev) => !prev);
            }}
          />
        </div>
      </div>

      {(showCreate || editing) && (
        <GuidelineForm
          key={
            editing
              ? `${editing.lookup_type}-${editing.lookup_id}-${editing.rollout_group}`
              : 'new'
          }
          complaintType={complaintType}
          initial={editing}
          onSaved={fetchGuidelines}
        />
      )}

      {isLoading && <IvaSkeleton />}

      {!isLoading &&
        Array.isArray(visibleGuidelines) &&
        visibleGuidelines.length === 0 && (
          <div className='tw-bg-white tw-p-8 tw-rounded-2xl tw-border tw-border-solid tw-border-grey-200 tw-text-[1.4rem] tw-text-grey-500'>
            No guidelines found for this complaint type.
          </div>
        )}

      {!isLoading &&
        Array.isArray(visibleGuidelines) &&
        visibleGuidelines.map((guideline) => (
          <GuidelineCard
            key={`${guideline.lookup_type}-${guideline.lookup_id}`}
            complaintType={complaintType}
            guideline={guideline}
            onPublished={fetchGuidelines}
            onEdit={(g) => {
              setShowCreate(false);
              setEditing(g);
            }}
          />
        ))}
    </div>
  );
};

export default GuidelinesSection;
